import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Wordmark } from "@/components/Brand";
import { UserButton } from "@clerk/clerk-react";
import { toast } from "sonner";
import { Check, ChevronLeft, Search, Sparkles, X } from "lucide-react";
import { useApi } from "@/lib/api";
import { CITIES, formatCityLabel } from "@/data/cities";
import NotMe from "@/components/NotMe";
import ConnectTikTok from "@/components/ConnectTikTok";
import { MAX_TAGS, NICHES } from "@/data/niches";


const HANDLE_RULES = {
    instagram: { re: /^[a-z0-9._]{1,30}$/, min: 1, max: 30, prefix: "instagram.com/" },
    tiktok: { re: /^[a-z0-9._]{2,24}$/, min: 2, max: 24, prefix: "tiktok.com/@" },
    youtube: { re: /^[a-z0-9._-]{3,30}$/, min: 3, max: 30, prefix: "youtube.com/@" },
    linkedin: { re: /^[a-z0-9_%-]{3,100}$/, min: 3, max: 100, prefix: "linkedin.com/in/" },
};

const SAMOONY_RE = /^[a-z0-9._]{2,30}$/;

const RESERVED_WORDS = [
    "about", "api", "app", "help", "login", "signup",
    "settings", "terms", "privacy", "admin", "samoony",
];

const asciiFold = (s) =>
    s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
const normHandle = (s) => asciiFold(s).toLowerCase().replace(/[^a-z0-9]/g, "");

const RESERVED_SET = new Set([
    ...RESERVED_WORDS.map(normHandle),
    ...CITIES.map((c) => normHandle(c.city)),
]);

const TIKTOK_URL_RE = /^https?:\/\/(?:www\.|vm\.|m\.)?tiktok\.com\/.+/i;

const cleanHandle = (v) => v.replace(/^@+/, "").trim().toLowerCase();

// A pasted LinkedIn profile URL becomes its slug.
const cleanLinkedIn = (v) => {
    const s = cleanHandle(v);
    const m = s.match(/linkedin\.com\/in\/([^/?#\s]+)/);
    return (m ? m[1] : s).replace(/\/+$/, "");
};

const emptyState = {
    is_adult: null,
    display_name: "",
    creator_name: "",
    link_in_bio: "",
    handles: { instagram: "", tiktok: "", youtube: "", linkedin: "" },
    samoony_handle: "",
    tiktok_video_urls: ["", "", "", "", "", "", "", "", "", ""],
    niches: [],
    primary_niche: "",
    city: null,
    tags: [],
    suggested: false,
    instagram_verified: false,
    consented: false,
};

const CONSENT_VERSION = "v1.2";

function StepDots({ step }) {
    const n = step === "verify" ? 1 : step;
    return (
        <div data-testid="onboarding-step-indicator" className="flex items-center gap-2">
            {[1, 2, 3].map((i) => (
                <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                        i === n ? "w-8 bg-ink" : i < n ? "w-4 bg-ink/40" : "w-4 bg-ink/15"
                    }`}
                />
            ))}
        </div>
    );
}

function HandleInput({ platform, label, value, onChange, testid }) {
    const rule = HANDLE_RULES[platform];
    const allowed =
        platform === "linkedin"
            ? "letters, numbers, dash or underscore"
            : `letters, numbers, dot${platform === "youtube" ? ", dash" : ""} or underscore`;
    const error =
        value && !rule.re.test(value)
            ? `Use ${rule.min === rule.max ? `${rule.min}` : `${rule.min} to ${rule.max}`} characters, ${allowed}.`
            : "";
    const clean = platform === "linkedin" ? cleanLinkedIn : cleanHandle;
    return (
        <label className="block">
            <div className="samoony-label mb-2">{label}</div>
            <div
                className={`flex items-stretch rounded-lg border bg-white overflow-hidden transition-colors ${
                    error ? "border-signal" : "border-black/10 focus-within:border-ink"
                } focus-within:shadow-[0_0_0_3px_rgba(255,46,126,0.18)]`}
            >
                <span className="font-mono text-sm text-black/50 self-center pl-4 pr-1">
                    {rule.prefix}
                </span>
                <input
                    data-testid={testid}
                    value={value}
                    onChange={(e) => onChange(clean(e.target.value))}
                    placeholder={platform === "linkedin" ? "your-name" : "yourhandle"}
                    className="flex-1 bg-transparent px-2 py-3 outline-none text-base"
                    style={{ minHeight: 56 }}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    // Room for a pasted LinkedIn URL before it is cut to the slug.
                    maxLength={platform === "linkedin" ? 200 : rule.max}
                />
            </div>
            {error && (
                <div
                    data-testid={`${testid}-error`}
                    className="mt-2 text-xs text-signal"
                >
                    {error}
                </div>
            )}
        </label>
    );
}

function samoonyCandidates(handles) {
    const seen = new Set();
    const out = [];
    for (const key of ["instagram", "tiktok", "youtube", "linkedin"]) {
        let v = handles[key];
        if (!v) continue;
        // LinkedIn slugs use dashes, which Samoony handles do not allow.
        if (key === "linkedin") v = v.replace(/-/g, ".").slice(0, 30).replace(/\.+$/, "");
        if (!SAMOONY_RE.test(v)) continue;
        if (RESERVED_SET.has(normHandle(v))) continue;
        if (seen.has(v)) continue;
        seen.add(v);
        out.push({ value: v, from: key });
    }
    return out;
}

function StepOne({ data, setData, onNext, publicBaseUrl }) {
    const candidates = useMemo(() => samoonyCandidates(data.handles), [data.handles]);
    const [handlePicked, setHandlePicked] = useState(false);

    // Default to the Instagram handle whenever one is available and the user
    // has not explicitly chosen a different candidate. Also fix invalid state
    // (current pick no longer in candidates).
    useEffect(() => {
        const igCandidate = candidates.find((c) => c.from === "instagram");
        const current = candidates.find((c) => c.value === data.samoony_handle);
        if (!current) {
            setData((d) => ({
                ...d,
                samoony_handle:
                    igCandidate?.value || candidates[0]?.value || "",
            }));
            return;
        }
        if (
            !handlePicked &&
            igCandidate &&
            data.samoony_handle !== igCandidate.value
        ) {
            setData((d) => ({ ...d, samoony_handle: igCandidate.value }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(candidates)]);

    const perHandleValid = (platform) => {
        const v = data.handles[platform];
        if (!v) return true;
        return HANDLE_RULES[platform].re.test(v);
    };
    const allHandlesValid =
        perHandleValid("instagram") &&
        perHandleValid("tiktok") &&
        perHandleValid("youtube") &&
        perHandleValid("linkedin");
    const hasAnyHandle =
        !!data.handles.instagram ||
        !!data.handles.tiktok ||
        !!data.handles.youtube ||
        !!data.handles.linkedin;

    // TikTok on its own needs a connected account: it is how we read the
    // videos and confirm the handle.
    const [tiktok, setTiktok] = useState(null);
    const onTikTokStatus = (st) => {
        setTiktok(st);
        if (st?.connected && st.username) {
            setData((d) =>
                d.handles.tiktok === st.username
                    ? d
                    : { ...d, handles: { ...d.handles, tiktok: st.username } },
            );
        }
    };
    const tiktokOnly =
        !!data.handles.tiktok && !data.handles.instagram && !data.handles.youtube;
    const tiktokConnected =
        !!tiktok?.connected && tiktok.username === (data.handles.tiktok || "").trim();
    const tiktokOk = !tiktokOnly || tiktokConnected;

    const canContinue =
        data.is_adult === true &&
        data.consented === true &&
        data.display_name.trim().length > 0 &&
        hasAnyHandle &&
        allHandlesValid &&
        tiktokOk &&
        !!data.samoony_handle;

    return (
        <div className="space-y-6 animate-fade-up">
            <div>
                <div className="samoony-label mb-2">Step 1 of 3</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                    Who are you online?
                </h1>
                <p className="text-sm text-black/60">
                    Add at least one handle. We clean it as you type.
                </p>
            </div>

            <div>
                <div className="samoony-label mb-2">Are you 18 or older?</div>
                <div className="flex gap-2">
                    {[
                        { k: true, label: "Yes" },
                        { k: false, label: "No" },
                    ].map((o) => (
                        <button
                            key={String(o.k)}
                            type="button"
                            data-testid={`onboarding-is-adult-${o.k ? "yes" : "no"}`}
                            data-selected={data.is_adult === o.k}
                            onClick={() => setData({ ...data, is_adult: o.k })}
                            className="samoony-chip flex-1"
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
                {data.is_adult === false && (
                    <p className="mt-2 text-xs text-signal">
                        Samoony is only for creators who are 18 or older.
                    </p>
                )}
            </div>

            {data.is_adult === true && (
                <div data-testid="onboarding-consent">
                    <div className="samoony-label mb-2">What you're agreeing to</div>
                    <p className="text-sm text-black/70">
                        We build and keep your page updated from your public posts, we may create
                        and manage profiles for you on other sites, you can undo updates or
                        remove everything anytime, and you must be 18 or older. Read our{" "}
                        <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline decoration-signal underline-offset-2"
                        >
                            terms
                        </a>
                        {" "}and{" "}
                        <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="onboarding-consent-privacy"
                            className="underline decoration-signal underline-offset-2"
                        >
                            privacy policy
                        </a>
                        .
                    </p>
                    <label className="mt-3 flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="onboarding-consent-checkbox"
                            checked={data.consented === true}
                            onChange={(e) =>
                                setData({ ...data, consented: e.target.checked })
                            }
                            className="mt-1 h-4 w-4 accent-signal"
                        />
                        <span className="text-sm text-ink">I agree.</span>
                    </label>
                </div>
            )}

            <label className="block">
                <div className="samoony-label mb-2">Full name</div>
                <input
                    data-testid="onboarding-full-name"
                    className="samoony-input"
                    value={data.display_name}
                    onChange={(e) => setData({ ...data, display_name: e.target.value })}
                    placeholder="First and last name"
                    autoComplete="name"
                    maxLength={80}
                />
                {data.display_name.trim().length === 0 && (
                    <p className="mt-1 text-xs text-black/50">
                        We use this when we write about you.
                    </p>
                )}
            </label>

            <label className="block">
                <div className="samoony-label mb-2">Creator name (optional)</div>
                <input
                    data-testid="onboarding-creator-name"
                    className="samoony-input"
                    value={data.creator_name}
                    onChange={(e) => setData({ ...data, creator_name: e.target.value })}
                    placeholder="e.g. Cory-Ate-Cha"
                    maxLength={80}
                />
            </label>

            <HandleInput
                platform="instagram"
                label="Instagram handle"
                value={data.handles.instagram}
                onChange={(v) => setData({ ...data, handles: { ...data.handles, instagram: v } })}
                testid="onboarding-handle-instagram"
            />
            <HandleInput
                platform="tiktok"
                label="TikTok handle"
                value={data.handles.tiktok}
                onChange={(v) => setData({ ...data, handles: { ...data.handles, tiktok: v } })}
                testid="onboarding-handle-tiktok"
            />
            <div data-testid="onboarding-tiktok-connect" className="-mt-3">
                <ConnectTikTok onStatus={onTikTokStatus} />
                {tiktokOnly && tiktok?.configured && !tiktokConnected && (
                    <p className="text-xs text-black/60 mt-2">
                        Connect TikTok to continue with TikTok only. It's how we read your
                        videos.
                    </p>
                )}
            </div>
            <HandleInput
                platform="youtube"
                label="YouTube handle"
                value={data.handles.youtube}
                onChange={(v) => setData({ ...data, handles: { ...data.handles, youtube: v } })}
                testid="onboarding-handle-youtube"
            />
            <HandleInput
                platform="linkedin"
                label="LinkedIn profile"
                value={data.handles.linkedin}
                onChange={(v) => setData({ ...data, handles: { ...data.handles, linkedin: v } })}
                testid="onboarding-handle-linkedin"
            />

            <label className="block">
                <div className="samoony-label mb-2">Website or link in bio (optional)</div>
                <input
                    data-testid="onboarding-link-in-bio"
                    className="samoony-input"
                    value={data.link_in_bio}
                    onChange={(e) => setData({ ...data, link_in_bio: e.target.value })}
                    placeholder="https://..."
                />
            </label>

            <div>
                <div className="samoony-label mb-2">Your Samoony handle</div>
                {candidates.length === 0 ? (
                    <p
                        data-testid="onboarding-samoony-empty"
                        className="text-xs text-black/50"
                    >
                        Add a valid Instagram, TikTok, YouTube or LinkedIn handle above.
                        One of them becomes your Samoony handle.
                    </p>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-2">
                            {candidates.map((c) => (
                                <button
                                    key={c.value + c.from}
                                    type="button"
                                    data-testid={`onboarding-samoony-pick-${c.from}`}
                                    data-selected={data.samoony_handle === c.value}
                                    onClick={() => {
                                        setHandlePicked(true);
                                        setData({ ...data, samoony_handle: c.value });
                                    }}
                                    className="samoony-chip"
                                >
                                    @{c.value}
                                    <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                                        from {c.from}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {data.samoony_handle && (
                            <div
                                data-testid="onboarding-samoony-preview"
                                className="mt-3 font-mono text-xs text-black/60"
                            >
                                {((publicBaseUrl || "").replace(/^https?:\/\/(www\.)?/, "") || "samoony.com")}/@{data.samoony_handle}
                            </div>
                        )}
                    </>
                )}
            </div>

            <button
                data-testid="onboarding-step1-next"
                disabled={!canContinue}
                onClick={onNext}
                className="samoony-pill samoony-pill-primary w-full disabled:opacity-30 disabled:cursor-not-allowed"
            >
                Continue
            </button>
            {!hasAnyHandle && (
                <p className="text-xs text-black/50 text-center">
                    Add at least one handle to continue.
                </p>
            )}
        </div>
    );
}

function StepVerifyInstagram({ data, setData, onBack, onNext }) {
    const { request } = useApi();
    const ig = (data.handles.instagram || "").trim();
    const [code, setCode] = useState("");
    const [verified, setVerified] = useState(false);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [message, setMessage] = useState("");
    const [messageTone, setMessageTone] = useState("muted");
    const [expiresAt, setExpiresAt] = useState("");

    useEffect(() => {
        if (!ig) return;
        let active = true;
        request("/verify/instagram/start", {
            method: "POST",
            body: { instagram_handle: ig },
        })
            .then((res) => {
                if (!active) return;
                if (res?.verified) {
                    setVerified(true);
                    setMessage("Verified. You can remove the code from your bio now.");
                    setMessageTone("ok");
                } else {
                    setCode(res?.code || "");
                    setExpiresAt(res?.expires_at || "");
                }
            })
            .catch((err) => {
                if (!active) return;
                setMessage(err?.message || "Could not start verification. Try again.");
                setMessageTone("error");
            })
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            toast.success("Code copied.");
        } catch {
            // noop
        }
    };

    const check = async () => {
        if (checking || verified) return;
        setChecking(true);
        setMessage("");
        try {
            const res = await request("/verify/instagram/check", {
                method: "POST",
                body: { instagram_handle: ig },
            });
            if (res?.verified) {
                setVerified(true);
                setMessage("Verified. You can remove the code from your bio now.");
                setMessageTone("ok");
                setData({ ...data, instagram_verified: true });
            } else {
                setMessage(res?.message || "We could not see the code yet. Bios can take a minute to update.");
                setMessageTone("muted");
            }
        } catch (err) {
            setMessage(err?.message || "Something went wrong. Try again.");
            setMessageTone("error");
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="step-verify-instagram">
            <div>
                <h2 className="samoony-h2 text-ink">Verify your Instagram</h2>
                <p className="text-sm text-black/60 mt-2">
                    Add this code to your Instagram bio, then tap Check. We never post anything.
                </p>
            </div>

            <div className="bg-white border border-black/10 rounded-2xl px-5 sm:px-6 py-5">
                <div className="samoony-label mb-2">Your code</div>
                {loading ? (
                    <div className="text-sm text-black/50">Loading...</div>
                ) : (
                    <div className="flex items-center gap-3">
                        <code
                            data-testid="verify-code"
                            className="font-mono text-lg tracking-wider bg-black/5 rounded-lg px-3 py-2 select-all"
                        >
                            {code || "unavailable"}
                        </code>
                        {code && (
                            <button
                                data-testid="verify-copy"
                                onClick={copy}
                                className="samoony-pill samoony-pill-ghost"
                            >
                                Copy
                            </button>
                        )}
                    </div>
                )}
                <div className="text-xs text-black/50 mt-3">
                    Handle: <span className="font-mono">@{ig || "..."}</span>
                </div>
                {expiresAt && !verified && (
                    <div className="text-xs text-black/50 mt-1">
                        This code is valid for 30 minutes.
                    </div>
                )}
            </div>

            {message && (
                <div
                    data-testid="verify-message"
                    className={
                        messageTone === "ok"
                            ? "text-sm text-ink"
                            : messageTone === "error"
                            ? "text-sm text-signal"
                            : "text-sm text-black/60"
                    }
                >
                    {message}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    data-testid="verify-back"
                    onClick={onBack}
                    className="samoony-pill samoony-pill-ghost"
                >
                    <ChevronLeft size={18} /> Back
                </button>
                {!verified ? (
                    <button
                        data-testid="verify-check"
                        onClick={check}
                        disabled={checking || loading || !code}
                        className="samoony-pill samoony-pill-primary flex-1 disabled:opacity-30"
                    >
                        {checking ? "Checking..." : "Check"}
                    </button>
                ) : (
                    <button
                        data-testid="verify-continue"
                        onClick={onNext}
                        className="samoony-pill samoony-pill-primary flex-1"
                    >
                        <Check size={18} /> Continue
                    </button>
                )}
            </div>
        </div>
    );
}

function StepNiche({ data, setData, onBack, onNext }) {
    const { request } = useApi();
    const [suggesting, setSuggesting] = useState(false);
    const [insight, setInsight] = useState(null);
    const [tagInput, setTagInput] = useState("");

    // First visit with an Instagram handle and nothing picked yet: read the
    // recent posts and pre-select lane and topics. Best effort, never blocks.
    useEffect(() => {
        const ig = (data.handles.instagram || "").trim();
        if (!ig || data.suggested || data.niches.length > 0) return;
        let active = true;
        setSuggesting(true);
        request("/onboarding/suggest-niches", { method: "POST", body: { instagram: ig } })
            .then((res) => {
                if (!active) return;
                const niches = (res?.niches || []).filter((n) => NICHES.includes(n)).slice(0, 3);
                const primary = NICHES.includes(res?.primary_niche) ? res.primary_niche : niches[0] || "";
                const tags = (res?.tags || []).slice(0, MAX_TAGS);
                setData((prev) => ({
                    ...prev,
                    suggested: true,
                    niches: prev.niches.length ? prev.niches : niches,
                    primary_niche: prev.primary_niche || primary,
                    tags: prev.tags.length ? prev.tags : tags,
                }));
                setInsight({
                    found: niches.length > 0,
                    reason: res?.reason || "",
                    summary: res?.summary || "",
                    postsRead: res?.posts_read || 0,
                });
            })
            .catch(() => {
                if (active) setData((prev) => ({ ...prev, suggested: true }));
            })
            .finally(() => active && setSuggesting(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addTag = () => {
        const v = tagInput.replace(/\s+/g, " ").trim().replace(/^#/, "");
        if (v.length < 2) return;
        if (data.tags.some((t) => t.toLowerCase() === v.toLowerCase())) {
            setTagInput("");
            return;
        }
        if (data.tags.length >= MAX_TAGS) {
            toast(`You can add up to ${MAX_TAGS} tags.`);
            return;
        }
        setData({ ...data, tags: [...data.tags, v.slice(0, 40)] });
        setTagInput("");
    };

    const removeTag = (t) => setData({ ...data, tags: data.tags.filter((x) => x !== t) });

    const toggle = (n) => {
        if (data.niches.includes(n)) {
            const next = data.niches.filter((x) => x !== n);
            setData({
                ...data,
                niches: next,
                primary_niche:
                    data.primary_niche === n ? next[0] || "" : data.primary_niche,
            });
        } else if (data.niches.length < 3) {
            const next = [...data.niches, n];
            setData({
                ...data,
                niches: next,
                primary_niche: data.primary_niche || n,
            });
        } else {
            toast("You can pick up to 3 niches.");
        }
    };

    const canContinue = data.niches.length > 0 && data.primary_niche.length > 0;
    // Lanes come from the posts and are fixed. The full picker only shows
    // when we could not read the posts (private, no posts, no Instagram).
    const lanesFixed = data.niches.length > 0 && !(insight && !insight.found);

    return (
        <div className="space-y-6 animate-fade-up">
            <div>
                <div className="samoony-label mb-2">Step 2 of 3</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                    {lanesFixed ? "Your lanes" : "What is your lane?"}
                </h1>
                {!suggesting && !lanesFixed && (
                    <p className="text-sm text-black/60">
                        Pick up to 3. Tap a selected one again to make it your primary.
                    </p>
                )}
                {suggesting && (
                    <div
                        data-testid="onboarding-niche-suggesting"
                        className="mt-3 flex items-center gap-2 text-xs text-black/60"
                    >
                        <Sparkles size={14} className="text-signal" />
                        Reading your recent posts...
                    </div>
                )}
                {!suggesting && insight && !insight.found && (
                    <div
                        data-testid="onboarding-niche-no-signal"
                        className="mt-3 text-xs text-black/60"
                    >
                        {insight.reason === "private"
                            ? "Your account is private, so we can't read your posts. Pick your lane below."
                            : "We couldn't tell your lane from your posts yet. Pick it below."}
                    </div>
                )}
                {!suggesting && insight?.found && (
                    <div
                        data-testid="onboarding-niche-from-posts"
                        className="mt-3 rounded-xl bg-white border border-black/10 p-3"
                    >
                        <div className="flex items-center gap-2 text-xs text-black/60">
                            <Sparkles size={14} className="text-signal" />
                            From your last {insight.postsRead} posts
                        </div>
                        {insight.summary && (
                            <div className="mt-1 text-sm text-ink">{insight.summary}</div>
                        )}
                    </div>
                )}
            </div>

            {lanesFixed && (
                <div className="flex flex-wrap gap-2" data-testid="onboarding-lanes-fixed">
                    {[
                        data.primary_niche,
                        ...data.niches.filter((n) => n !== data.primary_niche),
                    ].map((n) => {
                        const primary = data.primary_niche === n;
                        return (
                            <span
                                key={n}
                                data-testid={`onboarding-niche-${n.toLowerCase().replace(/\s+/g, "-")}`}
                                data-selected="true"
                                data-primary={primary}
                                className="samoony-chip"
                            >
                                {primary && <Check size={14} />}
                                {n}
                                {primary && (
                                    <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">
                                        primary
                                    </span>
                                )}
                            </span>
                        );
                    })}
                </div>
            )}
            {lanesFixed && (
                <NotMe
                    testid="onboarding-not-me"
                    shown={{
                        primary_niche: data.primary_niche,
                        niches: data.niches,
                        tags: data.tags,
                        summary: insight?.summary || null,
                        instagram: data.handles.instagram || null,
                    }}
                />
            )}

            {!suggesting && !lanesFixed && (
                <div className="flex flex-wrap gap-2">
                    {NICHES.map((n) => {
                        const selected = data.niches.includes(n);
                        const primary = data.primary_niche === n;
                        return (
                            <button
                                key={n}
                                type="button"
                                data-testid={`onboarding-niche-${n.toLowerCase().replace(/\s+/g, "-")}`}
                                data-selected={selected}
                                data-primary={primary}
                                onClick={() => {
                                    if (selected && !primary) {
                                        setData({ ...data, primary_niche: n });
                                    } else {
                                        toggle(n);
                                    }
                                }}
                                className="samoony-chip"
                            >
                                {primary && <Check size={14} />}
                                {n}
                                {primary && (
                                    <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">
                                        primary
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {!lanesFixed && data.niches.length > 0 && (
                <div className="text-xs text-black/60">
                    Primary niche:{" "}
                    <span className="font-mono text-ink">{data.primary_niche || "none"}</span>
                </div>
            )}

            <div>
                <div className="samoony-label mb-2">
                    {insight?.found ? "Topics we found in your posts" : "What you're known for"}
                </div>
                <p className="text-sm text-black/60 mb-3">
                    Up to {MAX_TAGS} topics. These become the searches we check for your name.
                </p>
                <div className="flex flex-wrap gap-2 mb-3" data-testid="onboarding-tags">
                    {data.tags.map((t) => (
                        <span
                            key={t}
                            data-testid={`onboarding-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                            className="samoony-chip"
                        >
                            {t}
                            <button
                                type="button"
                                aria-label={`Remove ${t}`}
                                onClick={() => removeTag(t)}
                                className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-black/10"
                            >
                                <X size={12} />
                            </button>
                        </span>
                    ))}
                    {data.tags.length === 0 && !suggesting && (
                        <span className="text-xs text-black/40">No topics yet. Add one below.</span>
                    )}
                </div>
                <div className="flex gap-2">
                    <input
                        data-testid="onboarding-tag-input"
                        className="samoony-input flex-1"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addTag();
                            }
                        }}
                        placeholder="e.g. Brunch spots"
                        maxLength={40}
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        data-testid="onboarding-tag-add"
                        onClick={addTag}
                        disabled={tagInput.trim().length < 2}
                        className="samoony-chip disabled:opacity-30"
                    >
                        Add
                    </button>
                </div>
            </div>

            <div className="flex gap-3 pt-2">
                <button
                    data-testid="onboarding-step2-back"
                    onClick={onBack}
                    className="samoony-pill samoony-pill-ghost"
                >
                    <ChevronLeft size={18} /> Back
                </button>
                <button
                    data-testid="onboarding-step2-next"
                    disabled={!canContinue}
                    onClick={onNext}
                    className="samoony-pill samoony-pill-primary flex-1 disabled:opacity-30"
                >
                    Continue
                </button>
            </div>
        </div>
    );
}

function StepCity({ data, setData, onBack, onSubmit, submitting }) {
    const initial = data.city ? formatCityLabel(data.city) : "";
    const [query, setQuery] = useState(initial);
    const [open, setOpen] = useState(false);

    const trimmed = query.trim();

    const results = useMemo(() => {
        const q = trimmed.toLowerCase();
        if (!q) return [];
        return CITIES.filter((c) => {
            const hay = `${c.city} ${c.region} ${c.country}`.toLowerCase();
            return hay.includes(q);
        }).slice(0, 8);
    }, [trimmed]);

    const pick = (entry) => {
        setData({ ...data, city: entry });
        setQuery(formatCityLabel(entry));
        setOpen(false);
    };

    // A city from the list is required. It drives the page content, the
    // leader comparison and every tracked search prompt.
    const canSubmit = !!data.city && !submitting;

    return (
        <div className="space-y-6 animate-fade-up">
            <div>
                <div className="samoony-label mb-2">Step 3 of 3</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                    Where are you based?
                </h1>
                <p className="text-sm text-black/60">
                    Start typing. If your city is not listed, we will save what you type.
                </p>
            </div>

            <div className="relative">
                <div className="relative">
                    <Search
                        size={18}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40 pointer-events-none"
                    />
                    <input
                        data-testid="onboarding-city-input"
                        className="samoony-input pl-11"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                            if (data.city) setData({ ...data, city: null });
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder="Search cities"
                        autoComplete="off"
                    />
                </div>

                {open && (results.length > 0 || trimmed.length >= 2) && (
                    <div
                        data-testid="onboarding-city-results"
                        className="absolute z-10 mt-2 w-full bg-white border border-black/10 rounded-xl overflow-hidden shadow-lg"
                    >
                        {results.map((c) => (
                            <button
                                key={`${c.city}-${c.region}-${c.country}`}
                                type="button"
                                data-testid={`onboarding-city-option-${c.city.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => pick(c)}
                                className="w-full text-left px-4 py-3 hover:bg-paper flex items-center justify-between gap-4 min-h-[44px]"
                            >
                                <div>
                                    <div className="font-medium">
                                        {c.city}, {c.regionCode || c.region}
                                    </div>
                                    <div className="text-xs text-black/50">
                                        {c.region}, {c.country}
                                    </div>
                                </div>
                                <span className="font-mono text-[10px] uppercase tracking-widest text-black/40">
                                    {c.country}
                                </span>
                            </button>
                        ))}
                        {results.length === 0 && trimmed.length >= 2 && (
                            <div
                                data-testid="onboarding-city-no-match"
                                className="px-4 py-3 min-h-[44px]"
                            >
                                <div className="font-medium">No match yet</div>
                                <div className="text-xs text-black/50">
                                    Try the nearest big city. We use it for your
                                    page and for where we check if you're named.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="flex gap-3 pt-2">
                <button
                    data-testid="onboarding-step3-back"
                    onClick={onBack}
                    className="samoony-pill samoony-pill-ghost"
                >
                    <ChevronLeft size={18} /> Back
                </button>
                <button
                    data-testid="onboarding-submit"
                    disabled={!canSubmit}
                    onClick={onSubmit}
                    className="samoony-pill samoony-pill-primary flex-1 disabled:opacity-30"
                >
                    {submitting ? "Saving..." : "Save profile"}
                </button>
            </div>
        </div>
    );
}

// Each step has its own URL (/onboarding?step=lanes) so a refresh or the
// browser back button keeps the creator where they were.
const STEP_SLUGS = { 1: "", verify: "verify", 2: "lanes", 3: "city" };
const STEP_BY_SLUG = { verify: "verify", lanes: 2, city: 3 };
// Answers are only saved on the last step, so keep a draft for refreshes.
const DRAFT_KEY = "samoony.onboarding.draft";
const readDraft = () => {
    try {
        const raw = sessionStorage.getItem(DRAFT_KEY);
        return raw ? { ...emptyState, ...JSON.parse(raw) } : null;
    } catch {
        return null;
    }
};
const writeDraft = (d) => {
    try {
        if (d) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
        else sessionStorage.removeItem(DRAFT_KEY);
    } catch {
        /* storage blocked: refresh starts from the saved profile */
    }
};

export default function Onboarding() {
    const navigate = useNavigate();
    const { request } = useApi();
    const [params, setParams] = useSearchParams();
    const step = STEP_BY_SLUG[params.get("step")] || 1;
    const setStep = (next, replace = false) => {
        const slug = STEP_SLUGS[next];
        setParams(slug ? { step: slug } : {}, { replace });
        window.scrollTo(0, 0);
    };
    const [data, setData] = useState(emptyState);
    const [submitting, setSubmitting] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [publicBaseUrl, setPublicBaseUrl] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        let active = true;
        request("/me")
            .then((res) => {
                if (!active) return;
                setPublicBaseUrl(res?.public_base_url || "");
                setIsAdmin(!!res?.is_admin);
                if (res && res.creator) {
                    const c = res.creator;
                    const hint = res.instagram_hint || null;
                    const hintFull = (hint?.full_name || "").trim();
                    const matched = c.city
                        ? CITIES.find(
                              (x) =>
                                  x.city.toLowerCase() === String(c.city).toLowerCase() &&
                                  (!c.country ||
                                      x.country.toLowerCase() === String(c.country).toLowerCase()),
                          )
                        : null;
                    setData({
                        is_adult: c.is_adult === true ? true : null,
                        display_name:
                            (c.display_name || "").trim() ||
                            hintFull ||
                            (c.first_name || "").trim(),
                        creator_name: (c.creator_name || "").trim() || hintFull || "",
                        link_in_bio: c.link_in_bio || "",
                        handles: {
                            instagram: c.handles?.instagram || "",
                            tiktok: c.handles?.tiktok || "",
                            youtube: c.handles?.youtube || "",
                            linkedin: c.handles?.linkedin || "",
                        },
                        samoony_handle: c.samoony_handle || "",
                        tiktok_video_urls: [
                            ...(c.tiktok_video_urls || []),
                            "", "", "", "", "",
                        ].slice(0, 5),
                        niches: c.niches || [],
                        primary_niche: c.primary_niche || "",
                        tags: c.tags || [],
                        suggested: (c.niches || []).length > 0,
                        city: matched || null,
                        instagram_verified: !!c.instagram_verified_at,
                        consented: !!c.consented_at,
                    });
                }
            })
            .catch(() => {})
            .finally(() => {
                if (!active) return;
                const draft = readDraft();
                if (draft) setData(draft);
                setLoaded(true);
            });
        return () => {
            active = false;
        };
    }, [request]);

    useEffect(() => {
        if (loaded) writeDraft(data);
    }, [loaded, data]);

    // A refreshed URL can point past what the saved data allows; walk back
    // to the first step that still needs input.
    useEffect(() => {
        if (!loaded || step === 1) return;
        const ig = (data.handles.instagram || "").trim();
        if (!data.samoony_handle) setStep(1, true);
        else if (step === "verify" && (!ig || data.instagram_verified)) setStep(2, true);
        else if (step !== "verify" && ig && !data.instagram_verified) setStep("verify", true);
        else if (step === 3 && data.niches.length === 0) setStep(2, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded]);

    const submit = async () => {
        if (!data.city) {
            toast.error("Pick your city from the list.");
            return;
        }
        const chosenCity = {
            city: data.city.city,
            region: data.city.region,
            country: data.city.country,
        };

        const filledTikTokUrls = data.tiktok_video_urls.map((u) => u.trim()).filter(Boolean);

        setSubmitting(true);
        try {
            const saveRes = await request("/onboarding", {
                method: "POST",
                body: {
                    is_adult: data.is_adult === true,
                    display_name: data.display_name.trim(),
                    first_name: data.display_name.trim().split(/\s+/)[0],
                    creator_name: data.creator_name.trim() || null,
                    link_in_bio: data.link_in_bio.trim() || null,
                    handles: {
                        instagram: data.handles.instagram.trim() || null,
                        tiktok: data.handles.tiktok.trim() || null,
                        youtube: data.handles.youtube.trim() || null,
                        linkedin: data.handles.linkedin.trim() || null,
                    },
                    samoony_handle: data.samoony_handle,
                    tiktok_video_urls: [],
                    niches: data.niches,
                    primary_niche: data.primary_niche,
                    tags: data.tags,
                    city: chosenCity.city,
                    region: chosenCity.region,
                    country: chosenCity.country,
                    consent_version: CONSENT_VERSION,
                    consented: data.consented === true,
                },
            });
            writeDraft(null);

            // Claim path: a matching demo was attached and its page is live.
            if (saveRes?.claimed && saveRes?.published) {
                toast.success("Welcome. Your page is live.");
                navigate("/dashboard");
                return;
            }

            // Pending-ownership path (no verified Instagram, waiting on staff
            // confirmation): skip the build and show the waiting tile on
            // /profile instead. No build, no draft, no review.
            if (saveRes?.creator?.ownership_status === "pending_staff") {
                toast.success("Profile saved.");
                navigate("/profile");
                return;
            }

            // Re-editing: if the creator already has a page draft or approved page,
            // do not start a new build. Only first-time saves kick off /build.
            let hasDraft = false;
            try {
                const existing = await request("/pages/draft");
                hasDraft = !!existing?.draft;
            } catch {
                hasDraft = false;
            }
            if (hasDraft) {
                toast.success("Saved.");
                navigate("/profile");
            } else {
                toast.success("Profile saved.");
                try {
                    await request("/build", { method: "POST" });
                } catch {
                    // 429 or other failure is fine; /build shows the current status.
                }
                navigate("/build");
            }
        } catch (err) {
            toast.error(err.message || "Could not save your profile.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper">
            <header className="flex items-center justify-between px-5 py-4 sm:px-8 sm:py-6 max-w-xl mx-auto">
                <Link to="/" className="no-underline">
                    <Wordmark height={26} />
                </Link>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <Link
                            to="/admin/creators"
                            data-testid="onboarding-admin-link"
                            className="samoony-chip"
                        >
                            Admin
                        </Link>
                    )}
                    <UserButton afterSignOutUrl="/" />
                </div>
            </header>

            <main className="px-5 pb-16 sm:px-8 max-w-xl mx-auto">
                <div className="mb-8">
                    <StepDots step={step} />
                </div>

                {!loaded ? (
                    <div className="text-sm text-black/50">Loading...</div>
                ) : step === 1 ? (
                    <StepOne
                        data={data}
                        setData={setData}
                        publicBaseUrl={publicBaseUrl}
                        onNext={() => {
                            const ig = (data.handles.instagram || "").trim();
                            if (ig && !data.instagram_verified) {
                                setStep("verify");
                            } else {
                                setStep(2);
                            }
                        }}
                    />
                ) : step === "verify" ? (
                    <StepVerifyInstagram
                        data={data}
                        setData={setData}
                        onBack={() => setStep(1)}
                        onNext={() => setStep(2)}
                    />
                ) : step === 2 ? (
                    <StepNiche
                        data={data}
                        setData={setData}
                        onBack={() => setStep(1)}
                        onNext={() => setStep(3)}
                    />
                ) : (
                    <StepCity
                        data={data}
                        setData={setData}
                        onBack={() => setStep(2)}
                        onSubmit={submit}
                        submitting={submitting}
                    />
                )}
            </main>
        </div>
    );
}
